import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../Layout/DashboardLayout";
import DashboardTopbar from "../../Layout/DashboardTopbar";
import useTokenBalance from "../../hooks/useTokenBalance";
import { useAuthContext } from "../../Context/AuthContext";
import { useAlertContext } from "../../Context/AlertContext";
import { fetchAvailableTokenLotsForCurrentUser } from "../../utils/getTokenApi";
import "./manage-credits.css";

const parsePurchaseDate = (value) => {
  if (value == null || value === "") return null;
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isFinite(time) ? time : null;
};

const formatTokenQuantity = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  if (Number.isInteger(num)) return String(num);
  return String(num);
};

const lotRowKey = (lot, index) =>
  `${lot.Product_name || "product"}-${lot.LOT_Local_Time || index}-${lot.LOT_UTC_Expiration || ""}`;

const ManageCredits = () => {
  const { t } = useTranslation("common");
  const { user } = useAuthContext();
  const { setError } = useAlertContext();
  const { tokenBalance } = useTokenBalance();
  const [lotList, setLotList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseDateSort, setPurchaseDateSort] = useState("desc");

  const loadLots = useCallback(async () => {
    setLoading(true);
    try {
      const { lotList: list } = await fetchAvailableTokenLotsForCurrentUser();
      setLotList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to load token lots:", error);
      setLotList([]);
      setError(error.message || t("Unable to load token credits. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [setError, t]);

  useEffect(() => {
    if (!user) return undefined;
    loadLots();
    return undefined;
  }, [user, loadLots]);

  const sortedLots = useMemo(() => {
    const rows = [...lotList];
    rows.sort((a, b) => {
      const aTime = parsePurchaseDate(a.LOT_Local_Time);
      const bTime = parsePurchaseDate(b.LOT_Local_Time);
      if (aTime == null && bTime == null) return 0;
      if (aTime == null) return 1;
      if (bTime == null) return -1;
      return purchaseDateSort === "desc" ? bTime - aTime : aTime - bTime;
    });
    return rows;
  }, [lotList, purchaseDateSort]);

  const togglePurchaseDateSort = () => {
    setPurchaseDateSort((current) => (current === "desc" ? "asc" : "desc"));
  };

  const purchaseDateSortIndicator = purchaseDateSort === "desc" ? "▼" : "▲";

  return (
    <DashboardLayout>
      <div className="dashboard-page manage-credits-page">
        <DashboardTopbar title={t("Manage Credits")} tokenBalance={tokenBalance} />

        <div className="page-content">
          <div className="page-header">
            <h1>{t("Manage Credits")}</h1>
            <p>{t("View your purchased token lots and expiration dates.")}</p>
          </div>

          <div className="card manage-credits-card">
            {loading ? (
              <div className="dashboard-loading">{t("Fetching...")}</div>
            ) : sortedLots.length === 0 ? (
              <div className="dashboard-empty">{t("No token credits found.")}</div>
            ) : (
              <div className="table-scroll">
                <table className="data-table manage-credits-table">
                  <thead>
                    <tr>
                      <th>{t("Product Name")}</th>
                      <th>{t("Token Quantity")}</th>
                      <th>
                        <button
                          type="button"
                          className="manage-credits-sort-btn"
                          onClick={togglePurchaseDateSort}
                          aria-sort={
                            purchaseDateSort === "desc" ? "descending" : "ascending"
                          }>
                          {t("Purchase Date")} {purchaseDateSortIndicator}
                        </button>
                      </th>
                      <th>{t("Token Expire On")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLots.map((lot, index) => (
                      <tr key={lotRowKey(lot, index)}>
                        <td className="manage-credits-product-cell">
                          {lot.Product_name || "—"}
                        </td>
                        <td>{formatTokenQuantity(lot.LOT_quantity)}</td>
                        <td>{lot.LOT_Local_Time || "—"}</td>
                        <td>{lot.LOT_UTC_Expiration || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ManageCredits;
